import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { ApiService } from 'app/shared/api.service';
import Swal from 'sweetalert2';

export interface ApiJobDepartment {
    id: string;
    name: string;
    slug: string;
}

export interface ApiJobImage {
    id: number;
    url: string;
    name: string;
}

export interface ApiJobItem {
    id: string;
    department_id?: string;
    department?: ApiJobDepartment;
    location: string;
    title: string;
    slug: string;
    job_type: string;
    schedule_type?: string;
    event_name?: string;
    compensation: string;
    description: string;
    responsibilities: string[];
    requirements: string[];
    benefits: string[];
    is_active?: boolean;
    is_featured?: boolean;
    posted_at?: string;
    closed_at?: string;
    sort_order?: number;
    images?: ApiJobImage[];
    created_at?: string;
}

export interface JobOpening {
    id: string;
    slug: string;
    title: string;
    department: string;
    departmentSlug: string;
    location: string;
    locationSlug: string;
    type: string;
    scheduleType: string;
    eventName: string;
    status: 'Aktif' | 'Segera';
    compensation: string;
    eventDate: string;
    bannerImage: string;
    shortDescription: string;
    description: string;
    responsibilities: string[];
    requirements: string[];
    benefits: string[];
    aboutTheTeam: string;
    aboutVisidea: string;
    rawApiData?: ApiJobItem;
}

@Component({
    selector: 'app-career',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule],
    templateUrl: './career.component.html',
    styleUrls: ['./career.component.scss']
})
export class CareerComponent implements OnInit {
    whatsappNumber = '6282260084847';

    // Loading & Error States
    isLoadingJobs: boolean = false;
    isLoadingDetail: boolean = false;
    errorMessage: string | null = null;

    // Route state
    currentJobDetail: JobOpening | null = null;
    currentJobParam: string | null = null;

    // Filters
    searchQuery: string = '';
    selectedDepartment: string = 'all';
    selectedLocation: string = 'all';

    // Dynamic Filter Options
    departments: { id: string; name: string }[] = [{ id: 'all', name: 'Semua Departemen' }];
    locations: { id: string; name: string }[] = [{ id: 'all', name: 'Semua Lokasi' }];

    // Application Modal State
    isApplyModalOpen: boolean = false;
    isSubmitting: boolean = false;
    selectedCvFile: File | null = null;
    selectedCvFileName: string = '';

    // Application Form
    applicationForm = {
        fullName: '',
        whatsapp: '',
        email: '',
        instagram: '',
        portfolioUrl: '',
        experience: '',
        motivation: ''
    };

    // List of Job Openings (Purely dynamic from API)
    jobOpenings: JobOpening[] = [];

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private apiService: ApiService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.fetchJobs();

        this.route.paramMap.subscribe(params => {
            const paramId = params.get('id');
            this.currentJobParam = paramId;

            if (paramId) {
                this.loadJobDetail(paramId);
            } else {
                this.currentJobDetail = null;
            }

            if (typeof window !== 'undefined') {
                window.scrollTo({ top: 0, behavior: 'instant' });
            }
        });
    }

    async fetchJobs(): Promise<void> {
        this.isLoadingJobs = true;
        this.errorMessage = null;

        try {
            const apiEndpoint = '/api/v1/front/public/jobs/list?paginate=false&search=&page=&per_page=';
            let response: any = await this.apiService.get(apiEndpoint, false);

            if (!response || !response.success || !response.data?.list) {
                try {
                    const devRes = await fetch(`https://dev.api.visideastudio.com${apiEndpoint}`);
                    if (devRes.ok) {
                        response = await devRes.json();
                    }
                } catch (e) {
                    console.error('Fallback fetch jobs error:', e);
                }
            }

            if (response && response.data && Array.isArray(response.data.list)) {
                this.jobOpenings = response.data.list.map((item: ApiJobItem) => this.mapJobItem(item));
                this.populateFilterOptions();

                // If currently on a detail page, sync the detail view
                if (this.currentJobParam) {
                    this.loadJobDetail(this.currentJobParam);
                }
            } else {
                this.jobOpenings = [];
                this.populateFilterOptions();
            }
        } catch (error) {
            console.error('Error fetching jobs:', error);
            this.jobOpenings = [];
            this.populateFilterOptions();
        } finally {
            this.isLoadingJobs = false;
            this.cdr.detectChanges();
        }
    }

    async loadJobDetail(slugOrId: string): Promise<void> {
        const foundLocal = this.jobOpenings.find(
            j => j.id === slugOrId || j.slug === slugOrId || String(j.rawApiData?.id) === slugOrId
        );

        if (foundLocal) {
            this.currentJobDetail = foundLocal;
            this.cdr.detectChanges();
            return;
        }

        // Fetch from API directly by slug or id
        this.isLoadingDetail = true;
        try {
            const apiEndpoint = `/api/v1/front/public/jobs/${encodeURIComponent(slugOrId)}`;
            let response: any = await this.apiService.get(apiEndpoint, false);

            if (!response || !response.success || !response.data) {
                try {
                    const devRes = await fetch(`https://dev.api.visideastudio.com${apiEndpoint}`);
                    if (devRes.ok) {
                        response = await devRes.json();
                    }
                } catch (e) {
                    console.error('Fallback single job fetch error:', e);
                }
            }

            if (response && response.data && response.data.title) {
                this.currentJobDetail = this.mapJobItem(response.data);
            }
        } catch (error) {
            console.error(`Error loading job detail (${slugOrId}):`, error);
        } finally {
            this.isLoadingDetail = false;
            this.cdr.detectChanges();
        }
    }

    private mapJobItem(item: ApiJobItem): JobOpening {
        const deptName = item.department?.name || 'Photobooth - Event Operations';
        const deptSlug = item.department?.slug || this.slugify(deptName);
        const loc = item.location || 'Bandung';
        const locSlug = this.slugify(loc);
        const imgUrl = (item.images && item.images.length > 0 && item.images[0].url)
            ? item.images[0].url
            : '/assets/visidea/photo1.jpg';

        const schedule = item.schedule_type || 'Day Shift';
        const eventName = item.event_name || 'Event Visidea';
        const eventDateCombined = `${eventName} (${schedule})`;

        return {
            id: item.id || item.slug,
            slug: item.slug || item.id,
            title: item.title || 'Lowongan Visidea Studio',
            department: deptName,
            departmentSlug: deptSlug,
            location: loc,
            locationSlug: locSlug,
            type: item.job_type || 'Freelance',
            scheduleType: schedule,
            eventName: eventName,
            status: item.is_active !== false ? 'Aktif' : 'Segera',
            compensation: item.compensation || 'Fee Harian Kompetitif + Uang Makan',
            eventDate: eventDateCombined,
            bannerImage: imgUrl,
            shortDescription: item.description ? item.description.slice(0, 160) + '...' : '',
            description: item.description || '',
            responsibilities: Array.isArray(item.responsibilities) ? item.responsibilities : [],
            requirements: Array.isArray(item.requirements) ? item.requirements : [],
            benefits: Array.isArray(item.benefits) ? item.benefits : [],
            aboutTheTeam: `Tim ${deptName} Visidea Studio adalah ujung tombak dalam menghadirkan pengalaman visual yang menyenangkan, cepat, dan berkesan di setiap event. Kami mengedepankan kolaborasi solid, keramahan tanpa batas, dan standar kualitas terbaik.`,
            aboutVisidea: 'Visidea Studio adalah ekosistem penyedia layanan event dan visual modern terintegrasi di Indonesia, mulai dari Interactive Photobooth, Videobooth 360, Undangan Digital, Digital Guestbook, hingga Smart Gate Ticketing. Kami berdedikasi menciptakan momen tak terlupakan bagi setiap perayaan dan acara bergengsi.',
            rawApiData: item
        };
    }

    private populateFilterOptions(): void {
        const deptMap = new Map<string, string>();
        const locMap = new Map<string, string>();

        this.jobOpenings.forEach(job => {
            if (job.departmentSlug && job.department) {
                deptMap.set(job.departmentSlug, job.department);
            }
            if (job.locationSlug && job.location) {
                locMap.set(job.locationSlug, job.location);
            }
        });

        this.departments = [
            { id: 'all', name: 'Semua Departemen' },
            ...Array.from(deptMap.entries()).map(([id, name]) => ({ id, name }))
        ];

        this.locations = [
            { id: 'all', name: 'Semua Lokasi' },
            ...Array.from(locMap.entries()).map(([id, name]) => ({ id, name }))
        ];
    }

    private slugify(text: string): string {
        return text
            .toString()
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[^\w\-]+/g, '')
            .replace(/\-\-+/g, '-');
    }

    get filteredJobs(): JobOpening[] {
        return this.jobOpenings.filter((job) => {
            const matchesQuery = !this.searchQuery ||
                job.title.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                job.department.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                job.location.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                job.description.toLowerCase().includes(this.searchQuery.toLowerCase());

            const matchesDepartment = this.selectedDepartment === 'all' ||
                job.departmentSlug === this.selectedDepartment;

            const matchesLocation = this.selectedLocation === 'all' ||
                job.locationSlug === this.selectedLocation;

            return matchesQuery && matchesDepartment && matchesLocation;
        });
    }

    get currentDepartmentLabel(): string {
        const found = this.departments.find(d => d.id === this.selectedDepartment);
        return found ? found.name.toLowerCase() : 'semua departemen';
    }

    get currentLocationLabel(): string {
        const found = this.locations.find(l => l.id === this.selectedLocation);
        return found ? found.name.toLowerCase() : 'semua lokasi';
    }

    goToJobDetail(job: JobOpening): void {
        const target = job.slug || job.id;
        this.router.navigate(['/career', target]);
    }

    backToJobList(): void {
        this.router.navigate(['/career']);
    }

    scrollToJobs(): void {
        if (typeof document !== 'undefined') {
            const el = document.getElementById('job-listings-section');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }

    openApplyModal(job: JobOpening, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        this.currentJobDetail = job;
        this.isApplyModalOpen = true;
    }

    closeApplyModal(): void {
        this.isApplyModalOpen = false;
        this.selectedCvFile = null;
        this.selectedCvFileName = '';
    }

    onFileSelected(event: any): void {
        const file = event.target.files && event.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                Swal.fire({
                    icon: 'error',
                    title: 'Ukuran File Terlalu Besar',
                    text: 'Ukuran file CV maksimal adalah 10MB. Silakan upload file yang lebih kecil.',
                    confirmButtonColor: '#024D3D'
                });
                return;
            }
            this.selectedCvFile = file;
            this.selectedCvFileName = file.name;
        }
    }

    applyViaWhatsApp(job: JobOpening, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }

        const msg = `Halo HR / Rekrutmen Visidea Studio,%0A%0A` +
            `Saya tertarik untuk melamar posisi:*%0A` +
            `📌 *${encodeURIComponent(job.title)}*%0A` +
            `🏢 *Departemen*: ${encodeURIComponent(job.department)}%0A` +
            `📍 *Lokasi*: ${encodeURIComponent(job.location)}%0A%0A` +
            `Saya ingin mengirimkan data diri dan CV/Portofolio saya untuk posisi ini. Mohon informasi terkait proses seleksi dan interview selanjutnya. Terima kasih!`;

        const url = `https://wa.me/${this.whatsappNumber}?text=${msg}`;
        if (typeof window !== 'undefined') {
            window.open(url, '_blank');
        }
    }

    async submitApplication(): Promise<void> {
        if (!this.applicationForm.fullName || !this.applicationForm.whatsapp) {
            Swal.fire({
                icon: 'warning',
                title: 'Data Belum Lengkap',
                text: 'Harap masukkan Nama Lengkap dan Nomor WhatsApp aktif Anda.',
                confirmButtonColor: '#024D3D'
            });
            return;
        }

        this.isSubmitting = true;

        try {
            const jobSlug = this.currentJobDetail?.slug || this.currentJobDetail?.id || '';
            const apiEndpoint = `/api/v1/front/public/jobs/${encodeURIComponent(jobSlug)}/apply`;

            const formData = new FormData();
            formData.append('full_name', this.applicationForm.fullName);
            formData.append('whatsapp_number', this.applicationForm.whatsapp);
            formData.append('email', this.applicationForm.email || '');
            formData.append('instagram_username', this.applicationForm.instagram || '');
            formData.append('portfolio_url', this.applicationForm.portfolioUrl || '');
            formData.append('experience', this.applicationForm.experience || 'Belum ada pengalaman formal');
            formData.append('motivation', this.applicationForm.motivation || 'Tertarik mengembangkan karier di industri event bersama Visidea Studio');
            
            if (this.selectedCvFile) {
                formData.append('cv', this.selectedCvFile, this.selectedCvFileName);
            }

            let response: any = await this.apiService.post(apiEndpoint, formData, false);

            if (!response || !response.success) {
                try {
                    const devRes = await fetch(`https://dev.api.visideastudio.com${apiEndpoint}`, {
                        method: 'POST',
                        body: formData
                    });
                    if (devRes.ok) {
                        response = await devRes.json();
                    }
                } catch (e) {
                    console.error('Direct apply fetch fallback error:', e);
                }
            }

            if (response && response.success) {
                this.isSubmitting = false;
                this.closeApplyModal();

                const jobTitle = this.currentJobDetail?.title || 'Posisi Freelance Visidea';
                const msg = `Halo Rekrutmen Visidea Studio,%0A%0A` +
                    `Saya telah mengirimkan formulir lamaran pekerjaan online untuk posisi:*%0A` +
                    `📌 *${encodeURIComponent(jobTitle)}*%0A%0A` +
                    `• *Nama Lengkap*: ${encodeURIComponent(this.applicationForm.fullName)}%0A` +
                    `• *No. WhatsApp*: ${encodeURIComponent(this.applicationForm.whatsapp)}%0A` +
                    `• *Email*: ${encodeURIComponent(this.applicationForm.email || '-')}%0A%0A` +
                    `Mohon informasinya terkait tahapan proses seleksi selanjutnya. Terima kasih!`;

                const waUrl = `https://wa.me/${this.whatsappNumber}?text=${msg}`;

                Swal.fire({
                    icon: 'success',
                    title: 'Lamaran Berhasil Terkirim!',
                    text: 'Data dan berkas CV Anda telah diterima oleh tim rekrutmen Visidea Studio. Anda dapat mengonfirmasi via WhatsApp untuk follow-up cepat.',
                    showCancelButton: true,
                    confirmButtonText: 'Konfirmasi via WhatsApp',
                    cancelButtonText: 'Selesai',
                    confirmButtonColor: '#024D3D',
                    cancelButtonColor: '#6B7280'
                }).then((result) => {
                    if (result.isConfirmed && typeof window !== 'undefined') {
                        window.open(waUrl, '_blank');
                    }
                });

                // Reset form
                this.applicationForm = {
                    fullName: '',
                    whatsapp: '',
                    email: '',
                    instagram: '',
                    portfolioUrl: '',
                    experience: '',
                    motivation: ''
                };
                this.selectedCvFile = null;
                this.selectedCvFileName = '';
            } else {
                const errMsg = response?.errors
                    ? Object.values(response.errors).flat().join('\n')
                    : (response?.message || 'Gagal mengirimkan lamaran. Silakan coba beberapa saat lagi.');

                Swal.fire({
                    icon: 'error',
                    title: 'Gagal Mengirim Lamaran',
                    text: errMsg,
                    confirmButtonColor: '#024D3D'
                });
            }
        } catch (error: any) {
            console.error('Error submitting job application:', error);
            Swal.fire({
                icon: 'error',
                title: 'Terjadi Kesalahan',
                text: 'Koneksi bermasalah atau server sedang sibuk. Silakan coba kembali.',
                confirmButtonColor: '#024D3D'
            });
        } finally {
            this.isSubmitting = false;
            this.cdr.detectChanges();
        }
    }
}
